import GoogleClassroomIcon from '@/assets/icons/google-classroom.svg?react';
import TurtleTeachIcon from '@/assets/icons/turtle-teach.svg?react';
import { SentryIcon } from '@/components/displays/CustomIcons';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CategoryIcon from '@mui/icons-material/Category';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import FaceIcon from '@mui/icons-material/Face';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import PeopleIcon from '@mui/icons-material/People';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import PlagiarismIcon from '@mui/icons-material/Plagiarism';
import RuleIcon from '@mui/icons-material/Rule';
import ScienceIcon from '@mui/icons-material/Science';
import WindowOutlinedIcon from '@mui/icons-material/WindowOutlined';

import { AuthProvider } from '@/providers/auth/AuthProvider';
import { CleverAuthProvider } from '@/providers/auth/CleverAuthProvider';
import { GoogleAuthProvider } from '@/providers/auth/GoogleAuthProvider';
import { useSession } from '@/providers/session/SessionProvider';
import { Box, SvgIcon } from '@mui/material';
import { enUS as muiCoreLocaleEn, zhCN as muiCoreLocaleZhCN, zhHK as muiCoreLocaleZhHK } from '@mui/material/locale';
import { StyledEngineProvider } from '@mui/material/styles';
import { enUS as muiDataGridLocalesEn, zhCN as muiDataGridLocalesZhCN, zhHK as muiDataGridLocalesZhHK } from '@mui/x-data-grid/locales';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { type Navigation } from '@toolpad/core/AppProvider';
import { ReactRouterAppProvider } from '@toolpad/core/react-router';
import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { FullScreenLoading } from './components/displays/FullScreenLoading';
import { EvaluationHandler } from './handlers/EvaluationHandler/EvaluationHandler';
import { ResumeClassroomHandler } from './handlers/ResumeClassroomHandler/ResumeClassroomHandler';
import { useMedia } from './hooks/useMedia/useMedia';
import { isSupportedLanguage, LanguageLoacle } from './i18n';
import { SubscriptionProvider } from './providers/subscription/SubscriptionProvider';
import customTheme from './themes/theme';

const muiCoreLocales = {
    enUS: muiCoreLocaleEn,
    zhHK: muiCoreLocaleZhHK,
    zhCN: muiCoreLocaleZhCN,
};

const muiDataGridLoales = {
    enUS: muiDataGridLocalesEn,
    zhHK: muiDataGridLocalesZhHK,
    zhCN: muiDataGridLocalesZhCN,
};
export default function App() {
    const { session } = useSession();
    const { t, i18n } = useTranslation();
    const { isMobile } = useMedia();

    const theme = React.useMemo(() => {
        const lang = i18n.language;
        if (isSupportedLanguage(lang)) {
            return customTheme(muiCoreLocales[LanguageLoacle[lang]], muiDataGridLoales[LanguageLoacle[lang]]);
        }

        return customTheme(muiCoreLocales['zhHK'], muiDataGridLoales['zhHK']);
    }, [i18n.language]);

    const navigation: Navigation = React.useMemo(() => {
        const userRole = session?.user?.role;

        if (userRole === 'student') {
            // Students only see account page
            return [
                {
                    segment: 'account',
                    title: t('account.title'),
                    icon: <PermIdentityIcon />,
                },
            ];
        }

        const lab: Navigation =
            import.meta.env.MODE === 'development'
                ? [
                      {
                          kind: 'divider',
                      },
                      {
                          segment: 'lab',
                          title: 'Lab',
                          icon: <ScienceIcon />,
                          children: [
                              {
                                  segment: 'playground',
                                  title: 'Playground',
                                  icon: <CategoryIcon />,
                              },
                              {
                                  segment: 'sentry',
                                  title: 'Sentry',
                                  icon: <SentryIcon />,
                              },
                              {
                                  segment: 'google-classroom',
                                  title: 'Google Classroom',
                                  icon: <SvgIcon component={GoogleClassroomIcon} inheritViewBox />,
                              },
                          ],
                      },
                      {
                          segment: 'ai-grader',
                          title: '智能批改',
                          icon: <PlagiarismIcon />,
                          children: [
                              {
                                  segment: 'assignments',
                                  title: '作業批改',
                                  icon: <AssignmentIcon />,
                              },
                              {
                                  segment: 'rubrics',
                                  title: '評分準則',
                                  icon: <RuleIcon />,
                              },
                          ],
                      },
                  ]
                : [];

        // Teachers see all navigation items
        return [
            {
                segment: 'lessons',
                title: t('lesson.title'),
                icon: <WindowOutlinedIcon />,
                pattern: 'lessons{/:lessonId}*',
            },
            {
                segment: 'students',
                title: t('student.title'),
                icon: <FaceIcon />,
            },
            {
                segment: 'classes',
                title: t('class.title'),
                icon: <PeopleIcon />,
            },
            {
                segment: 'homework',
                title: t('homework.title'),
                icon: <ImportContactsIcon />,
            },
            {
                segment: 'results',
                title: t('result.title'),
                icon: <EqualizerIcon />,
            },
            ...lab,
            {
                kind: 'divider',
            },
            {
                segment: 'account',
                title: t('account.title'),
                icon: <PermIdentityIcon />,
            },
        ];
    }, [t, session?.user?.role]);

    const googleOAuthClientId = useMemo(
        () => (import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ? import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID : '613497684609-glka5k5qsbr5gau2di6b14gs85j1djtb.apps.googleusercontent.com'),
        [],
    );

    return (
        <StyledEngineProvider injectFirst>
            <ReactRouterAppProvider
                navigation={navigation}
                session={session}
                theme={theme}
                branding={{
                    logo: (
                        <Box width='100%' height='100%' display='flex' alignItems='center'>
                            <TurtleTeachIcon />
                        </Box>
                    ),
                    title: isMobile ? '' : t('common.turtleTeach'),
                }}
            >
                <AuthProvider>
                    <GoogleOAuthProvider clientId={googleOAuthClientId}>
                        <GoogleAuthProvider>
                            <CleverAuthProvider>
                                <SubscriptionProvider>
                                    <React.Suspense fallback={<FullScreenLoading />}>
                                        <Outlet />
                                        <ResumeClassroomHandler />
                                        <EvaluationHandler />
                                    </React.Suspense>
                                </SubscriptionProvider>
                            </CleverAuthProvider>
                        </GoogleAuthProvider>
                    </GoogleOAuthProvider>
                </AuthProvider>
            </ReactRouterAppProvider>
        </StyledEngineProvider>
    );
}
